import useModifyRequest from '../../../../hooks/useModifyRequest';
import * as React from 'react';
import { logError } from '../../../../lib/debug-helpers';
import { FormCard } from '../../../../components/forms/FormComponents';
import TextField from '../../../../components/forms/TextField';
import strings from '../../../../strings/strings.json';
import { LearningObjective } from '../../../../models/LearningObjective';
import styled from 'styled-components';
import { sizes, theme } from '../../../../theme';
import AddIcon from '../../../../assets/icons/controls/AddIcon';
import useFormBlocker from 'components/forms/useFormBlocker';
import Notification, { NotificationType } from 'components/notifiers/Notification';
import NavButton from 'components/buttons/NavButton';
import useWindowWidth from 'hooks/useWindowWidth';
import {
    LeftNavButtonContainer,
    SaveButtonAndNotificationContainer,
    RightNavButtonContainer,
} from 'pages/build-course/shared-styles/courseBuilderFormComponents';
import { BuildCourseContext } from 'contextProviders/BuildCourseContext';
import { Course } from 'models/Course';
import { ErrorNotifierSlim } from 'components/notifiers/ErrorNotifierSlim';
import { EditModeSaveNotification } from 'pages/build-course/components/activityForms/editActivityForms/shared/SharedEditTaskComponents';
import Button from 'components/buttons/Button';

type State = 'clean' | 'dirty' | 'saved' | 'error' | 'errorNoObjectives' | 'errorNoDescription';

export default function CourseLearningObjectivesForm(): JSX.Element {
    const { setCourseState, refetchCourse, editMode, showIncompleteStepsWarning, setActiveStep } =
        React.useContext(BuildCourseContext)!;
    const course = React.useContext(BuildCourseContext).course!;

    const { isMobileWidth } = useWindowWidth();
    const { modifyData: updateObjectives, loading: updating } = useModifyRequest(
        `courses/${course.id}/learning-objectives`,
        'PUT',
    );

    const [objectives, setObjectives] = React.useState<LearningObjective[]>(course.learningObjectives);
    const [state, setState] = React.useState<State>('clean');
    useFormBlocker(state === 'dirty', strings.courseEditorPage.unsavedChangesPrompt);

    const addObjectiveButtonRef = React.useRef<HTMLButtonElement>(null);
    const saveButtonRef = React.useRef<HTMLButtonElement>(null);
    const inputRefs = React.useRef<(HTMLInputElement | null)[]>([]);

    const isFirstRender = React.useRef(true);

    React.useEffect(() => {
        if (isFirstRender.current) {
            setTimeout(() => {
                if (objectives.length > 0) {
                    inputRefs.current[0]?.focus();
                } else {
                    addObjectiveButtonRef.current?.focus();
                }
            });

            isFirstRender.current = false;
        }
    }, []); //eslint-disable-line

    React.useEffect(() => {
        const objectivesChanged =
            objectives.length !== course.learningObjectives.length ||
            objectives.some((o, i) => o.description !== course.learningObjectives[i].description);
        if (objectivesChanged) {
            setState('dirty');
        }
    }, [objectives]); //eslint-disable-line

    React.useEffect(() => {
        inputRefs.current = inputRefs.current.filter(Boolean);
    }, [objectives.length]);

    const saveObjectives = async () => {
        if (state === 'saved') return;

        if (objectives.length === 0) {
            setState('errorNoObjectives');
            addObjectiveButtonRef.current?.focus();
            return;
        }
        if (objectives.some((o) => o.description.trim().length === 0)) {
            setState('errorNoDescription');
            return;
        }

        if (editMode) {
            setCourseState((prev: Course | undefined) => ({
                ...prev!,
                learningObjectives: objectives.filter((o) => !!o.description),
            }));
        } else {
            const { errors } = await updateObjectives({
                learningObjectives: objectives,
            });
            if (errors && errors.length > 0) {
                logError(errors);
                setState('error');
                return;
            }

            await refetchCourse();
        }

        setState('saved');
        saveButtonRef.current?.focus();
    };

    function addNewObjective() {
        setObjectives((prev) => {
            const newObjective: LearningObjective = {
                description: '',
                order: prev.length, //newOrderNumber
            };
            return [...prev, newObjective];
        });

        setTimeout(() => {
            const lastIndex = inputRefs.current.length - 1;
            inputRefs.current[lastIndex]?.focus();
        });
    }

    function removeObjective(indexToRemove: number) {
        setObjectives((prev) => {
            const updated = prev.filter((_, i) => i !== indexToRemove).map((obj, i) => ({ ...obj, order: i }));

            setTimeout(() => {
                if (updated.length === 0) {
                    addObjectiveButtonRef.current?.focus();
                } else {
                    const focusIndex = indexToRemove > 0 ? indexToRemove - 1 : 0;
                    inputRefs.current[focusIndex]?.focus();
                }
            });

            return updated;
        });
    }

    function changeObjectiveDescription(indexToChange: number, description: string) {
        setObjectives((prevState) => {
            const updatedObjectives = [...prevState];
            updatedObjectives[indexToChange] = {
                ...updatedObjectives[indexToChange],
                description,
            };
            return updatedObjectives;
        });
    }

    function CourseHasAtLeastOneLearningObjective(course: Course): boolean {
        return course.learningObjectives.length >= 1;
    }

    return (
        <>
            {!CourseHasAtLeastOneLearningObjective(course) && showIncompleteStepsWarning && (
                <NotificationContainer>
                    <Notification
                        title={strings.courseEditorPage.noLearningObjectivesWarning.title}
                        notificationType={NotificationType.WARNING}
                        maxWidth="100%"
                    />
                </NotificationContainer>
            )}

            <Form as={'div'}>
                {!isMobileWidth && (
                    <LeftNavButtonContainer>
                        <NavButton
                            title={strings.courseLearningObjectivesPage.courseDetailsNavButton.title}
                            aria={strings.courseLearningObjectivesPage.courseDetailsNavButton.aria}
                            back
                            onClick={() => setActiveStep('courseDetails')}
                        />
                    </LeftNavButtonContainer>
                )}
                {objectives.map((objective, index) => {
                    return (
                        <ObjectiveCard key={index}>
                            <TextField
                                ref={(el) => (inputRefs.current[index] = el)}
                                fieldName={`learning-objective-${index + 1}`}
                                maxLength={500}
                                labelText={`${strings.courseLearningObjectivesPage.learningObjectiveInputLabel} ${index + 1}`}
                                inputAria={`${strings.courseLearningObjectivesPage.learningObjectiveInputLabelAria} ${index + 1}`}
                                value={objective.description}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                    changeObjectiveDescription(index, e.target.value)
                                }
                                errorMessage={undefined}
                            />
                            <Button
                                title={strings.courseLearningObjectivesPage.removeButton}
                                aria={`${strings.courseLearningObjectivesPage.removeButtonAria} ${index + 1}`}
                                colour={'BLACK'}
                                onClick={() => removeObjective(index)}
                            />
                        </ObjectiveCard>
                    );
                })}
                {objectives.length < 10 && (
                    <Button
                        ref={addObjectiveButtonRef}
                        title={strings.courseLearningObjectivesPage.addObjectiveButton}
                        aria={strings.courseLearningObjectivesPage.addObjectiveButtonAria}
                        icon={<AddIcon />}
                        onClick={() => addNewObjective()}
                        fullwidth
                    />
                )}
                <>
                    <SaveButtonAndNotificationContainer>
                        {state === 'saved' &&
                            (editMode ? (
                                <EditModeSaveNotification />
                            ) : (
                                <Notification
                                    notificationType={NotificationType.SUCCESS}
                                    title={strings.general.saveSuccess}
                                />
                            ))}
                        {state === 'error' && (
                            <ErrorNotifierSlim description={strings.courseLearningObjectivesPage.error} />
                        )}
                        {state === 'errorNoObjectives' && (
                            <ErrorNotifierSlim description={strings.editModeNotifiers.noLearningObjectives} />
                        )}
                        {state === 'errorNoDescription' && (
                            <ErrorNotifierSlim
                                description={strings.courseLearningObjectivesPage.learningObjectiveDescriptionRequired}
                            />
                        )}

                        <Button
                            ref={saveButtonRef}
                            title={strings.courseLearningObjectivesPage.submitButton}
                            aria={strings.courseLearningObjectivesPage.submitButtonAria}
                            onClick={saveObjectives}
                            disabled={editMode && state !== 'dirty'}
                            submitting={updating}
                            colour='PRIMARY'
                        />
                    </SaveButtonAndNotificationContainer>

                    {!isMobileWidth && (
                        <RightNavButtonContainer>
                            <NavButton
                                title={strings.courseLearningObjectivesPage.structureAndContentNavButton.title}
                                aria={strings.courseLearningObjectivesPage.structureAndContentNavButton.aria}
                                onClick={() => setActiveStep('courseStructureAndContent')}
                            />
                        </RightNavButtonContainer>
                    )}
                </>
            </Form>
        </>
    );
}

const Form = styled(FormCard)`
    width: 100%;
`;

const NotificationContainer = styled.div`
    width: 100%;
`;

const ObjectiveCard = styled.div`
    background-color: ${theme.cardSecondaryBackgroundColour};
    border-radius: ${sizes.borderRadiusLg};
    padding: ${sizes.spacingMd};
`;
